import React, { useEffect, useState, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { LogOut, LayoutDashboard, FileText, Image as ImageIcon, Mail, Settings as SettingsIcon, Users, Download, Trash2, CheckCircle2, Archive, Wrench } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import Logo from '../components/Logo';
import { Toaster } from '../components/ui/toaster';
import { toast } from '../hooks/use-toast';
import { adminMe, listSubmissions, patchSubmission, deleteSubmission, getSettings, updateSettings, createResource, changePassword, uploadUrl, getResources, deleteResource, uploadMedia } from '../lib/api';
import ServicesEditor from '../admin/ServicesEditor';
import IndustriesEditor from '../admin/IndustriesEditor';
import PagesEditor from '../admin/PagesEditor';
import MediaLibrary from '../admin/MediaLibrary';
import SEOEditor from '../admin/SEOEditor';

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [me, setMe] = useState(null);
  const [tab, setTab] = useState('overview');
  const [subs, setSubs] = useState({ items: [], total: 0, unread: 0 });
  const [settings, setSettings] = useState(null);
  const [resources, setResources] = useState([]);
  const [mediaPicker, setMediaPicker] = useState(null); // { onPick }

  const requireAuth = useCallback(async () => {
    const t = localStorage.getItem('aqc_admin_token');
    if (!t) return navigate('/admin/login');
    try {
      const info = await adminMe();
      setMe(info);
    } catch {
      localStorage.removeItem('aqc_admin_token');
      navigate('/admin/login');
    }
  }, [navigate]);

  const loadAll = useCallback(async () => {
    try {
      const [s, st, r] = await Promise.all([listSubmissions({ limit: 100 }), getSettings(), getResources()]);
      setSubs(s);
      setSettings(st || {});
      setResources(r || []);
    } catch (e) {
      // token might be expired
    }
  }, []);

  useEffect(() => {
    requireAuth().then(loadAll);
  }, [requireAuth, loadAll]);

  const logout = () => {
    localStorage.removeItem('aqc_admin_token');
    localStorage.removeItem('aqc_admin_email');
    navigate('/admin/login');
  };

  const menu = [
    { k: 'overview', label: 'Overview', icon: LayoutDashboard },
    { k: 'submissions', label: `Submissions (${subs.unread || 0})`, icon: Mail },
    { k: 'pages', label: 'Pages & Content', icon: FileText },
    { k: 'services', label: 'Services', icon: Wrench },
    { k: 'industries', label: 'Industries', icon: Users },
    { k: 'media', label: 'Media Library', icon: ImageIcon },
    { k: 'resources', label: 'Downloadable Resources', icon: Download },
    { k: 'seo', label: 'SEO Metadata', icon: SettingsIcon },
    { k: 'settings', label: 'Site Settings', icon: SettingsIcon },
    { k: 'account', label: 'Account', icon: SettingsIcon },
  ];

  const markRead = async (id, read = true) => {
    await patchSubmission(id, { read });
    await loadAll();
  };
  const archive = async (id) => {
    await patchSubmission(id, { archived: true });
    await loadAll();
  };
  const remove = async (id) => {
    await deleteSubmission(id);
    await loadAll();
  };

  return (
    <div className="min-h-screen bg-aqc-cream font-sans-body flex">
      <aside className="hidden md:flex w-64 shrink-0 bg-aqc-navy text-white flex-col">
        <div className="p-5 border-b border-white/10">
          <Logo variant="light" size={34} />
        </div>
        <nav className="flex-1 p-3 space-y-1">
          {menu.map((m) => {
            const Icon = m.icon;
            const active = tab === m.k;
            return (
              <button key={m.k} onClick={() => setTab(m.k)} className={`w-full flex items-center gap-2.5 px-3 py-2.5 rounded-md text-sm text-left transition-colors ${active ? 'bg-white/10 text-white' : 'text-white/75 hover:bg-white/5'}`}>
                <Icon size={16} /> {m.label}
              </button>
            );
          })}
        </nav>
        <div className="p-4 border-t border-white/10 text-xs text-white/60">
          Signed in as<br /><span className="text-white">{me?.email || '…'}</span>
        </div>
      </aside>

      <div className="flex-1 min-w-0">
        <header className="bg-white border-b border-aqc-line px-5 py-3.5 flex items-center justify-between">
          <div>
            <div className="text-xs uppercase tracking-[0.14em] text-aqc-muted">Admin</div>
            <h1 className="font-serif text-lg text-aqc-navy">Apex Quality Control — Dashboard</h1>
          </div>
          <div className="flex items-center gap-2">
            <Link to="/" className="text-sm text-aqc-navy underline">View site</Link>
            <Button variant="outline" onClick={logout} className="gap-1.5"><LogOut size={14} /> Log out</Button>
          </div>
        </header>

        <main className="p-6 lg:p-8">
          {tab === 'overview' && <Overview subs={subs} resources={resources} />}
          {tab === 'submissions' && (
            <Submissions items={subs.items} onRead={markRead} onArchive={archive} onDelete={remove} />
          )}
          {tab === 'settings' && <SettingsPanel settings={settings} onSaved={loadAll} onOpenMedia={setMediaPicker} />}
          {tab === 'resources' && <ResourcesPanel items={resources} onChange={loadAll} />}
          {tab === 'account' && <AccountPanel />}
          {tab === 'pages' && <PagesEditor />}
          {tab === 'services' && <ServicesEditor onOpenMedia={(onPick) => setMediaPicker({ onPick })} />}
          {tab === 'industries' && <IndustriesEditor onOpenMedia={(onPick) => setMediaPicker({ onPick })} />}
          {tab === 'media' && <MediaLibrary />}
          {tab === 'seo' && <SEOEditor onOpenMedia={setMediaPicker} />}
        </main>
      </div>
      {mediaPicker && (
        <MediaLibrary
          pickMode
          onPick={mediaPicker.onPick}
          onClose={() => setMediaPicker(null)}
        />
      )}
      <Toaster />
    </div>
  );
}

function Overview({ subs, resources }) {
  const stats = [
    { k: 'Submissions', v: subs.total || 0 },
    { k: 'Unread', v: subs.unread || 0 },
    { k: 'Resources', v: resources.length },
    { k: 'Services', v: 4 },
  ];
  return (
    <div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {stats.map((s) => (
          <div key={s.k} className="bg-white border border-aqc-line rounded-2xl p-5">
            <div className="text-xs uppercase tracking-[0.12em] text-aqc-muted">{s.k}</div>
            <div className="mt-1 font-serif text-3xl text-aqc-navy">{s.v}</div>
          </div>
        ))}
      </div>
      <div className="mt-6 bg-white border border-aqc-line rounded-2xl p-6">
        <h2 className="font-serif text-xl text-aqc-navy">Recent submissions</h2>
        {subs.items?.length === 0 ? (
          <p className="mt-3 text-sm text-aqc-muted">No submissions yet.</p>
        ) : (
          <ul className="mt-3 divide-y divide-aqc-line">
            {(subs.items || []).slice(0, 5).map((l) => (
              <li key={l.id} className="py-3">
                <div className="flex items-center justify-between">
                  <div className="font-medium text-aqc-navy">{l.name} {!l.read && <span className="ml-2 text-[10px] uppercase tracking-widest text-aqc-red">new</span>}</div>
                  <div className="text-xs text-aqc-muted">{new Date(l.created_at).toLocaleString()}</div>
                </div>
                <div className="text-sm text-aqc-muted">{l.email} · {l.service || 'Enquiry'}</div>
                <p className="text-sm mt-1 line-clamp-2">{l.message}</p>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

function Submissions({ items, onRead, onArchive, onDelete }) {
  if (!items || items.length === 0) {
    return (
      <div className="bg-white border border-aqc-line rounded-2xl p-8 text-center">
        <h2 className="font-serif text-2xl text-aqc-navy">No submissions yet</h2>
        <p className="mt-2 text-aqc-muted">Enquiries submitted from the contact form will appear here.</p>
      </div>
    );
  }
  return (
    <div className="space-y-3">
      {items.map((l) => (
        <div key={l.id} className={`bg-white border rounded-2xl p-5 ${l.read ? 'border-aqc-line' : 'border-aqc-red/40'}`}>
          <div className="flex items-start justify-between gap-4">
            <div>
              <div className="font-serif text-lg text-aqc-navy flex items-center gap-2">
                {l.name}
                {!l.read && <span className="text-[10px] uppercase tracking-widest text-aqc-red bg-aqc-red/10 px-2 py-0.5 rounded">new</span>}
                {l.archived && <span className="text-[10px] uppercase tracking-widest text-aqc-muted bg-aqc-muted/10 px-2 py-0.5 rounded">archived</span>}
              </div>
              <div className="text-sm text-aqc-muted mt-0.5">{l.email} · {l.phone || '—'} · {l.service || 'Enquiry'} {l.company ? `· ${l.company}` : ''}</div>
            </div>
            <div className="text-xs text-aqc-muted whitespace-nowrap">{new Date(l.created_at).toLocaleString()}</div>
          </div>
          <p className="mt-3 text-[0.95rem] whitespace-pre-wrap">{l.message}</p>
          <div className="mt-3 flex flex-wrap gap-2">
            {!l.read && <Button size="sm" variant="outline" onClick={() => onRead(l.id, true)} className="gap-1.5"><CheckCircle2 size={14} /> Mark read</Button>}
            {!l.archived && <Button size="sm" variant="outline" onClick={() => onArchive(l.id)} className="gap-1.5"><Archive size={14} /> Archive</Button>}
            <Button size="sm" variant="outline" onClick={() => onDelete(l.id)} className="gap-1.5 text-aqc-red hover:bg-aqc-red hover:text-white"><Trash2 size={14} /> Delete</Button>
          </div>
        </div>
      ))}
    </div>
  );
}

function SettingsPanel({ settings, onSaved, onOpenMedia }) {
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [logoUrl, setLogoUrl] = useState('');
  const [faviconUrl, setFaviconUrl] = useState('');
  useEffect(() => {
    if (settings?.company) {
      setForm({
        name: settings.company.name || '',
        email: settings.company.email || '',
        phone: settings.company.phone || '',
        phoneRaw: settings.company.phoneRaw || '',
        line1: settings.company.address?.line1 || '',
        line2: settings.company.address?.line2 || '',
        city: settings.company.address?.city || '',
        country: settings.company.address?.country || '',
        hours: settings.company.hours || '',
        linkedin: settings.socials?.linkedin || '',
        twitter: settings.socials?.twitter || '',
        facebook: settings.socials?.facebook || '',
      });
      setLogoUrl(settings.logo_url || '');
      setFaviconUrl(settings.favicon_url || '');
    }
  }, [settings]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const save = async () => {
    setSaving(true);
    try {
      await updateSettings({
        company: {
          name: form.name,
          email: form.email,
          phone: form.phone,
          phoneRaw: form.phoneRaw,
          hours: form.hours,
          address: { line1: form.line1, line2: form.line2, city: form.city, country: form.country },
        },
        socials: { linkedin: form.linkedin, twitter: form.twitter, facebook: form.facebook },
        logo_url: logoUrl,
        favicon_url: faviconUrl,
      });
      toast({ title: 'Settings saved' });
      onSaved && onSaved();
    } catch (e) {
      toast({ title: 'Save failed', description: e?.response?.data?.detail || 'Try again.' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-white border border-aqc-line rounded-2xl p-6 max-w-3xl">
      <h2 className="font-serif text-2xl text-aqc-navy">Site Settings</h2>
      <p className="text-sm text-aqc-muted mt-1">Company details, socials, logo & favicon shown across the public site.</p>

      <div className="mt-6 grid sm:grid-cols-2 gap-4">
        <div className="sm:col-span-2">
          <Label>Logo (transparent PNG/SVG)</Label>
          <div className="flex items-center gap-3 mt-1.5">
            <div className="h-14 w-14 rounded-lg border border-aqc-line bg-aqc-cream flex items-center justify-center overflow-hidden">
              {logoUrl ? <img src={uploadUrl(logoUrl)} alt="logo" className="max-h-full max-w-full object-contain" /> : <ImageIcon size={22} className="text-aqc-muted" />}
            </div>
            <Input value={logoUrl} onChange={(e) => setLogoUrl(e.target.value)} placeholder="URL or /api/uploads/..." />
            <Button type="button" variant="outline" onClick={() => onOpenMedia && onOpenMedia({ onPick: (u) => setLogoUrl(u) })} className="whitespace-nowrap gap-1.5"><ImageIcon size={14} /> Pick</Button>
          </div>
        </div>
        <div className="sm:col-span-2">
          <Label>Favicon</Label>
          <div className="flex items-center gap-3 mt-1.5">
            <div className="h-10 w-10 rounded-lg border border-aqc-line bg-aqc-cream flex items-center justify-center overflow-hidden">
              {faviconUrl ? <img src={uploadUrl(faviconUrl)} alt="favicon" className="max-h-full max-w-full object-contain" /> : <ImageIcon size={16} className="text-aqc-muted" />}
            </div>
            <Input value={faviconUrl} onChange={(e) => setFaviconUrl(e.target.value)} placeholder="URL or /api/uploads/..." />
            <Button type="button" variant="outline" onClick={() => onOpenMedia && onOpenMedia({ onPick: (u) => setFaviconUrl(u) })} className="whitespace-nowrap gap-1.5"><ImageIcon size={14} /> Pick</Button>
          </div>
        </div>

        <Field label="Company name" v={form.name} onChange={set('name')} />
        <Field label="Working hours" v={form.hours} onChange={set('hours')} />
        <Field label="Email" v={form.email} onChange={set('email')} />
        <Field label="Phone (display)" v={form.phone} onChange={set('phone')} />
        <Field label="Phone (raw for tel:)" v={form.phoneRaw} onChange={set('phoneRaw')} />
        <Field label="Address line 1" v={form.line1} onChange={set('line1')} />
        <Field label="Address line 2" v={form.line2} onChange={set('line2')} />
        <Field label="City" v={form.city} onChange={set('city')} />
        <Field label="Country" v={form.country} onChange={set('country')} />
        <Field label="LinkedIn URL" v={form.linkedin} onChange={set('linkedin')} />
        <Field label="Twitter URL" v={form.twitter} onChange={set('twitter')} />
        <Field label="Facebook URL" v={form.facebook} onChange={set('facebook')} />
      </div>
      <Button className="mt-6 bg-aqc-navy hover:bg-[hsl(var(--aqc-navy-2))] text-white" disabled={saving} onClick={save}>
        {saving ? 'Saving…' : 'Save changes'}
      </Button>
    </div>
  );
}

function Field({ label, v, onChange }) {
  return (
    <div>
      <Label>{label}</Label>
      <Input value={v || ''} onChange={onChange} className="mt-1.5" />
    </div>
  );
}

function ResourcesPanel({ items, onChange }) {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!title || !file) {
      toast({ title: 'Missing', description: 'Title and file are required.' });
      return;
    }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append('title', title);
      fd.append('description', description);
      fd.append('order', '0');
      fd.append('file', file);
      await createResource(fd);
      toast({ title: 'Resource uploaded' });
      setTitle(''); setDescription(''); setFile(null);
      onChange && onChange();
    } catch (err) {
      toast({ title: 'Upload failed', description: err?.response?.data?.detail || 'Try again.' });
    } finally {
      setBusy(false);
    }
  };

  const remove = async (id) => {
    await deleteResource(id);
    onChange && onChange();
  };

  return (
    <div className="grid lg:grid-cols-2 gap-6">
      <div className="bg-white border border-aqc-line rounded-2xl p-6">
        <h2 className="font-serif text-2xl text-aqc-navy">Add resource</h2>
        <form onSubmit={submit} className="mt-4 space-y-3">
          <div>
            <Label>Title</Label>
            <Input value={title} onChange={(e) => setTitle(e.target.value)} className="mt-1.5" />
          </div>
          <div>
            <Label>Description</Label>
            <Textarea value={description} onChange={(e) => setDescription(e.target.value)} className="mt-1.5" rows={3} />
          </div>
          <div>
            <Label>File (PDF)</Label>
            <input type="file" accept=".pdf" onChange={(e) => setFile(e.target.files?.[0] || null)} className="mt-1.5 block w-full text-sm" />
          </div>
          <Button type="submit" disabled={busy} className="bg-aqc-navy hover:bg-[hsl(var(--aqc-navy-2))] text-white">
            {busy ? 'Uploading…' : 'Upload'}
          </Button>
        </form>
      </div>
      <div className="bg-white border border-aqc-line rounded-2xl p-6">
        <h2 className="font-serif text-2xl text-aqc-navy">Uploaded resources</h2>
        {items.length === 0 ? (
          <p className="mt-3 text-sm text-aqc-muted">No resources yet.</p>
        ) : (
          <ul className="mt-3 divide-y divide-aqc-line">
            {items.map((r) => (
              <li key={r.id} className="py-3 flex items-start justify-between gap-3">
                <div>
                  <div className="font-medium text-aqc-navy">{r.title}</div>
                  <div className="text-xs text-aqc-muted mt-0.5">{r.meta}</div>
                  <a href={uploadUrl(r.url)} target="_blank" rel="noopener noreferrer" className="text-xs text-aqc-red">Open file</a>
                </div>
                <Button size="sm" variant="outline" onClick={() => remove(r.id)} className="text-aqc-red hover:bg-aqc-red hover:text-white gap-1.5"><Trash2 size={14} /> Delete</Button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

function AccountPanel() {
  const [oldPw, setOldPw] = useState('');
  const [newPw, setNewPw] = useState('');
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (newPw.length < 6) return toast({ title: 'Password too short', description: 'Use at least 6 characters.' });
    setBusy(true);
    try {
      await changePassword(oldPw, newPw);
      toast({ title: 'Password updated' });
      setOldPw(''); setNewPw('');
    } catch (e) {
      toast({ title: 'Failed', description: e?.response?.data?.detail || 'Please try again.' });
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="bg-white border border-aqc-line rounded-2xl p-6 max-w-md">
      <h2 className="font-serif text-2xl text-aqc-navy">Change password</h2>
      <form onSubmit={submit} className="mt-4 space-y-3">
        <div>
          <Label>Current password</Label>
          <Input type="password" value={oldPw} onChange={(e) => setOldPw(e.target.value)} className="mt-1.5" />
        </div>
        <div>
          <Label>New password</Label>
          <Input type="password" value={newPw} onChange={(e) => setNewPw(e.target.value)} className="mt-1.5" />
        </div>
        <Button type="submit" disabled={busy} className="bg-aqc-navy hover:bg-[hsl(var(--aqc-navy-2))] text-white">
          {busy ? 'Saving…' : 'Update password'}
        </Button>
      </form>
    </div>
  );
}
